import React, { useState } from 'react';
import { Button, Icon, Select } from 'Components';
import styles from 'LearningManager/styles/CourseAssignments.module.scss';
import { Assignment } from 'LearningManager/constants/CoursesAssignment.types';
import AssignmentCard from '../components/AssignmentCard';
import ToggleGroup from 'LearningManager/components/ToggleGroup';
import { useHistory } from 'react-router-dom';
import UnassignTrainingModal from 'LearningManager/components/UnassignTrainingModal';
import ViewDetailsDrawer from 'LearningManager/components/ViewDetailsDrawer';

const { Option } = Select;

const MOCK_ASSIGNMENTS: Assignment[] = [
	{
		id: '1',
		name: '[Assignment Name C]',
		type: 'One-Time',
		setupType: 'Quick Setup',
		status: 'Scheduled',
		courseCount: 4
	},
	{
		id: '2',
		name: '[Assignment Name C]',
		type: 'One-Time',
		setupType: 'Quick Setup',
		courseCount: 2
	},
	{
		id: '3',
		name: '[Assignment Name C]',
		type: 'Ongoing',
		setupType: 'Custom Setup',
		status: 'Scheduled',
		courseCount: 9
	},
	{
		id: '4',
		name: '[Assignment Name D]',
		type: 'Ongoing',
		setupType: 'Custom Setup',
		courseCount: 8
	}
];

const CourseAssignments: React.FC = () => {
	const [activeTab, setActiveTab] = useState<string>('1');
	const [selectedOrg, setSelectedOrg] = useState<string>('all');
	const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
	const [assignments, setAssignments] = useState<Assignment[]>(
		MOCK_ASSIGNMENTS
	);
	const [unassignTarget, setUnassignTarget] = useState<Assignment | null>(
		null
	);
	const [detailTarget, setDetailTarget] = useState<Assignment | null>(null);
	const history = useHistory();

	const handleCheck = (id: string, checked: boolean) => {
		setCheckedIds((prev) => {
			const next = new Set(prev);
			checked ? next.add(id) : next.delete(id);
			return next;
		});
	};

	const handleViewContent = (id: string) => {
		// Wire to router: history.push(`/learning-manager/assignments/${id}`)
		console.log('View content:', id);
	};

	const handleMenuAction = (id: string, action: string) => {
		const assignment = assignments.find((item) => item.id === id);

		if (action === 'edit') {
			const setupType = assignment?.setupType ?? 'Quick Setup';
			const params = new URLSearchParams({
				setupType,
				assignmentId: id
			});
			if (assignment?.status === 'Scheduled') {
				params.set('isScheduled', 'true');
			}

			history.push(`create-assignment?${params.toString()}`);
			return;
		}

		if (action === 'view-details' && assignment) {
			setDetailTarget(assignment);
			return;
		}

		if (action === 'unassign' && assignment) {
			setUnassignTarget(assignment);
			return;
		}

		console.log('Menu action:', action, 'on assignment:', id);
	};

	const handleConfirmUnassign = () => {
		if (!unassignTarget) return;

		setAssignments((prev) =>
			prev.filter((assignment) => assignment.id !== unassignTarget.id)
		);
		setCheckedIds((prev) => {
			const next = new Set(prev);
			next.delete(unassignTarget.id);
			return next;
		});
		setUnassignTarget(null);
	};

	const handleExport = () => {
		console.log('Export triggered');
	};

	const handleNewAssignment = () => {
		history.push('create-assignment');
	};

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<div className={styles.header__left}>
					<h1 className={styles.header__title}>Course assignments</h1>
					<p className={styles.header__subtitle}>
						Create, manage and track training assignments
					</p>
				</div>

				<div className={styles.header__actions}>
					<Button
						size="small"
						onClick={handleExport}
						className={styles.header__actions__exportBtn}
					>
						Export
					</Button>
					<Button
						size="small"
						className="cta-prominent"
						type="primary"
						icon={<Icon type="plus" role="img" />}
						onClick={handleNewAssignment}
					>
						New assignment
					</Button>
				</div>
			</div>

			<div className={styles.filtersContainer}>
				<ToggleGroup
					value={activeTab}
					onChange={(value) => setActiveTab(value)}
					options={[
						{ key: '1', label: 'Assignments' },
						{ key: '2', label: 'Courses' }
					]}
				/>

				<Select
					value={selectedOrg}
					onChange={setSelectedOrg}
					style={{ width: 180, color: '#000' }}
					size="small"
					className={styles.orgSelect}
				>
					<Option value="all">All organization</Option>
					<Option value="org1">Organization 1</Option>
					<Option value="org2">Organization 2</Option>
				</Select>
			</div>

			<div className={styles.listHeader}>
				<span className={styles.listHeader__label}>
					Assignments by organization ({assignments.length})
				</span>
			</div>

			{activeTab === '1' && (
				<div className={styles.list}>
					{assignments.map((assignment) => (
						<AssignmentCard
							key={assignment.id}
							assignment={assignment}
							checked={checkedIds.has(assignment.id)}
							onCheck={handleCheck}
							onViewContent={handleViewContent}
							onMenuAction={handleMenuAction}
						/>
					))}
				</div>
			)}

			{activeTab === '2' && <div className={styles.list}></div>}

			<div className={styles.footer}>Powered by Venza</div>
			<UnassignTrainingModal
				visible={Boolean(unassignTarget)}
				onSubmit={handleConfirmUnassign}
				onCancel={() => setUnassignTarget(null)}
			/>
			<ViewDetailsDrawer
				visible={Boolean(detailTarget)}
				detailTarget={detailTarget}
				onClose={() => setDetailTarget(null)}
			/>
		</div>
	);
};

export default CourseAssignments;
